import type { DayHours } from "@/components/business-profile/hours-editor"

// Shared form specs for the receptionist. Both the setup wizard steps and
// the edit-panel tabs render these — single source of truth for option
// lists, labels, and small helpers so the two views don't drift.
//
// Voice options come from the server (Agents::Retell::Voices) so the catalogue
// + preview URLs stay in sync with Retell. See @/components/agents/setup/types.

export const DEFAULT_GREETING =
  "Thank you for calling. How can I help you today?"

// IMP-490/492: there is no MessageChannel or delivery destination. Where a
// message goes is an account-level Notifications setting (IMP-375); whether the
// receptionist takes one at all is this single boolean.
//
// Agents set up before IMP-490 carry the legacy `message_channel`, so read it as
// the fallback — "none" was the owner switching messages off, anything else
// (including no key at all) means on. Mirrors the server-side rule in
// Agents::Receptionist::MessageSettings; the two must not drift.
export function readMessagesEnabled(data: Record<string, unknown>): boolean {
  const stored = data.messages_enabled
  if (typeof stored === "boolean") return stored
  if (stored === "true" || stored === "false") return stored === "true"
  return data.message_channel !== "none"
}

export type PhoneOption = "new" | "forward"

export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export function formatAreaCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, 3)
}

// IMP-491: the receptionist's transfer window. A saved schedule always wins;
// the prefill (mirrored server-side from the business hours) is only the
// starting point the first time the control is shown.
export function readHours(
  data: Record<string, unknown>,
  prefill: DayHours[],
): DayHours[] {
  const saved = data.handoff_hours
  return Array.isArray(saved) && saved.length > 0
    ? (saved as DayHours[])
    : prefill
}
